// [목적] AI 자연어 보정 제안 미리보기 다이얼로그 — before/after diff + changes 목록 + 적용/취소
// [이유] FlowLine dcc2c3cc FE 과업1 — useFolioRefine 훅의 propose 결과(RefineProposalResponse)를
//        받아 추가 항목 강조·삭제 항목 취소선으로 렌더하고, 적용 전 사용자 확인을 받는다.
//        MUI Dialog 가 Esc 닫기·포커스 트랩·aria-labelledby 를 기본 제공(ReportDialog.tsx 관례 재사용).
// [사이드 이팩트] 없음 — 상태는 모두 부모(useFolioRefine 훅)가 소유, 이 컴포넌트는 순수 렌더 + 콜백 위임.
// [수정 시 고려사항] citation(addr1/tel/usetime/restDate)은 TourAPI 원문 그대로 표시(CLAUDE §4, LLM 변형 금지
//                   — BE 가 이미 원문을 채워 보내므로 이 컴포넌트는 가공 없이 그대로 노출).

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { RefineDayItem, RefineProposalResponse } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;

export interface RefineProposalDialogProps {
  open: boolean;
  proposal: RefineProposalResponse | null;
  isProposing: boolean;
  proposeErrorMessage: string | null;
  isApplying: boolean;
  applyErrorMessage: string | null;
  onApply: () => void;
  onCancel: () => void;
}

interface DiffRow {
  item: RefineDayItem;
  status: 'kept' | 'added';
}

/**
 * [목적] before/after 의 items 를 citation.contentId 기준으로 비교해 추가/제거/유지 항목을 분류
 * [이유] BE 응답에 항목 diff 플래그가 없어(before/after 스냅샷 2벌만 제공) FE 가 계산해야 함.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] contentId 가 비어있는(직접 추가) 항목은 동일 키가 없다면 항상 "추가"로 분류됨 —
 *                   BE 가 향후 항목별 안정적 id 를 내려주면 contentId 대신 그 id 로 교체할 것.
 */
function diffDayItems(
  before: RefineDayItem[],
  after: RefineDayItem[],
): {
  removed: RefineDayItem[];
  rows: DiffRow[];
} {
  const beforeIds = new Set(before.map((it) => it.citation.contentId));
  const afterIds = new Set(after.map((it) => it.citation.contentId));
  const removed = before.filter((it) => !afterIds.has(it.citation.contentId));
  const rows: DiffRow[] = after.map((item) => ({
    item,
    status: beforeIds.has(item.citation.contentId) ? 'kept' : 'added',
  }));
  return { removed, rows };
}

/**
 * [목적] 제안 항목 1행 렌더 — 추가(강조)/제거(취소선)/유지(기본) 3가지 시각 상태
 * [이유] 작업 카드: "추가 항목 강조·삭제 항목 취소선"
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 색만으로 상태를 구분하지 않도록 "추가"/"삭제" 텍스트 배지를 함께 표시(WCAG 1.4.1).
 */
function DiffItemRow({ item, status }: DiffRow): JSX.Element {
  const isAdded = status === 'added';
  return (
    <Box
      sx={{
        p: `${tokens.spacing[2]}px`,
        borderRadius: `${tokens.radius.s}px`,
        border: `1px solid ${isAdded ? c.accent[500][L] : c.line.base[L]}`,
        bgcolor: isAdded ? `${c.accent[500][L]}14` : c.paper[0][L],
        mb: `${tokens.spacing[1]}px`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '2px' }}>
        {isAdded && (
          <Typography
            component="span"
            sx={{ fontSize: 10, fontWeight: 700, color: c.accent[500][L] }}
          >
            추가
          </Typography>
        )}
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: c.ink.high[L] }}>
          {item.citation.title ?? '(제목 없음)'}
        </Typography>
      </Box>
      {item.storyText && (
        <Typography sx={{ fontSize: 12, color: c.ink.mid[L], lineHeight: 1.5 }}>
          {item.storyText}
        </Typography>
      )}
      {item.citation.addr1 && (
        <Typography sx={{ fontSize: 11, color: c.ink.low[L], mt: '2px' }}>
          {item.citation.addr1}
          {item.citation.tel ? ` · ${item.citation.tel}` : ''}
        </Typography>
      )}
    </Box>
  );
}

/**
 * [목적] 제거된(before 에만 있던) 항목 표시 — 취소선 + "삭제" 배지
 * [이유] 위 함수와 분리해 removed 목록을 after 순서와 별도로 렌더(디자인: 제거 목록을 위에 모아 보여줌).
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 없음.
 */
function RemovedItemRow({ item }: { item: RefineDayItem }): JSX.Element {
  return (
    <Box
      sx={{
        p: `${tokens.spacing[2]}px`,
        borderRadius: `${tokens.radius.s}px`,
        border: `1px solid ${c.line.base[L]}`,
        bgcolor: c.paper[1][L],
        mb: `${tokens.spacing[1]}px`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          sx={{ fontSize: 10, fontWeight: 700, color: c.danger[500][L] }}
        >
          삭제
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.ink.low[L], textDecoration: 'line-through' }}>
          {item.citation.title ?? '(제목 없음)'}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * [목적] AI 보정 제안 미리보기 다이얼로그 본체
 * [이유] FolioEditPage/FolioMobile 공용 — useFolioRefine 훅 상태를 그대로 props 로 전달받아 렌더.
 * [사이드 이팩트] role="status" aria-live="polite" 영역이 로딩/완료/에러 전환을 스크린리더에 알림.
 * [수정 시 고려사항] Esc 닫기는 MUI Dialog 기본 동작(onClose) 그대로 사용 — 별도 keydown 핸들러 불필요.
 */
export function RefineProposalDialog({
  open,
  proposal,
  isProposing,
  proposeErrorMessage,
  isApplying,
  applyErrorMessage,
  onApply,
  onCancel,
}: RefineProposalDialogProps): JSX.Element {
  const diff = proposal ? diffDayItems(proposal.day.before.items, proposal.day.after.items) : null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="refine-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${tokens.radius.l}px`, bgcolor: c.paper[0][L] } }}
    >
      <DialogTitle
        id="refine-dialog-title"
        sx={{ fontSize: 16, fontWeight: 700, color: c.ink.high[L] }}
      >
        AI 보정 제안
      </DialogTitle>
      <DialogContent>
        <Box role="status" aria-live="polite">
          {isProposing && (
            <Typography sx={{ fontSize: 13, color: c.ink.mid[L] }}>
              AI가 일정을 다시 구성하고 있어요…
            </Typography>
          )}

          {proposeErrorMessage && (
            <Alert severity="error" role="alert" sx={{ mb: `${tokens.spacing[2]}px` }}>
              {proposeErrorMessage}
            </Alert>
          )}

          {applyErrorMessage && (
            <Alert severity="error" role="alert" sx={{ mb: `${tokens.spacing[2]}px` }}>
              {applyErrorMessage}
            </Alert>
          )}

          {proposal && diff && (
            <>
              {proposal.changes.length > 0 && (
                <Box
                  sx={{
                    mb: `${tokens.spacing[3]}px`,
                    p: `${tokens.spacing[2]}px`,
                    borderRadius: `${tokens.radius.s}px`,
                    bgcolor: c.paper[1][L],
                  }}
                >
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 700, color: c.ink.mid[L], mb: '4px' }}
                  >
                    변경 요약
                  </Typography>
                  {proposal.changes.map((change, i) => (
                    <Typography key={i} sx={{ fontSize: 12, color: c.ink.high[L] }}>
                      · {change}
                    </Typography>
                  ))}
                </Box>
              )}

              {diff.removed.length > 0 && (
                <Box sx={{ mb: `${tokens.spacing[2]}px` }}>
                  {diff.removed.map((item) => (
                    <RemovedItemRow key={item.citation.contentId} item={item} />
                  ))}
                </Box>
              )}

              <Box>
                {diff.rows.map(({ item, status }) => (
                  <DiffItemRow key={item.citation.contentId} item={item} status={status} />
                ))}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: `${tokens.spacing[4]}px`, pb: `${tokens.spacing[3]}px` }}>
        <Button onClick={onCancel} aria-label="AI 보정 취소" disabled={isApplying}>
          취소
        </Button>
        <Button
          onClick={onApply}
          variant="contained"
          aria-label="AI 보정 적용"
          disabled={!proposal || isProposing || isApplying}
          sx={{
            bgcolor: c.brand[500][L],
            // [수정: 디자인 개편] 비활성일 때도 파란 배경+흰색 글자가 그대로 남았다 — 회색 면으로 명시
            '&.Mui-disabled': { bgcolor: c.paper[2][L], color: c.ink.low[L] },
            '&:focus-visible': { outline: `2px solid ${c.brand[500][L]}`, outlineOffset: '2px' },
          }}
        >
          {isApplying ? '적용 중…' : '적용'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
